'use client';

import React, { useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faUserDoctor,
  faHospital,
} from '@fortawesome/pro-solid-svg-icons';

const options = [
  { label: 'Providers', value: 'hcp', icon: faUserDoctor },
  { label: 'Sites', value: 'site', icon: faHospital },
] as const;

export interface ProspectTypeToggleProps {
  value: 'hcp' | 'site';
  onChange: (value: 'hcp' | 'site') => void;
}

const ProspectTypeToggle: React.FC<ProspectTypeToggleProps> = ({
  value,
  onChange,
}): React.ReactElement => {
  const handleClick = useCallback(
    (newValue: 'hcp' | 'site') => {
      onChange(newValue);
    },
    [onChange]
  );

  return (
    <div
      className="@container/toggle relative z-[1] rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-center items-center bg-white h-9"
      data-testid="prospect-type-toggle-container"
    >
      <div
        className={`absolute h-full transition-all duration-300 ease-in-out rounded-full bg-sky-600 w-1/2 ${
          value === 'hcp' ? 'left-0' : 'left-1/2'
        }`}
      />

      {options.map((option, idx) => {
        const isActive = value === option.value;

        return (
          <div
            key={`${option.value}-${idx}`}
            className="w-1/2 rounded-full flex justify-center items-center z-10 h-full relative px-2 @[200px]/toggle:px-3 @[250px]/toggle:px-4"
            data-testid={`${option.value}-option-container`}
          >
            <button
              type="button"
              data-testid={`${option.value}-button`}
              className="w-full h-full rounded-full flex justify-center items-center gap-2 focus:outline-none cursor-pointer"
              aria-pressed={isActive}
              onClick={() => handleClick(option.value)}
              tabIndex={0}
            >
              <div
                className={`${
                  isActive
                    ? 'text-white font-bold'
                    : 'text-secondary font-normal'
                } text-sm font-['Inter'] leading-tight transition-all duration-300 flex items-center gap-2`}
              >
                <Icon
                  icon={option.icon}
                  className="text-xs transition-transform duration-300"
                />
                <span className="hidden @[200px]/toggle:inline">
                  {option.label}
                </span>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ProspectTypeToggle;
